import React, { useCallback, useEffect, useState } from 'react';
import { Drawer, Table, Input, DatePicker, Button, Tag } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useLazyQuery } from '@apollo/client';
import {
    GET_PICKUP_FAILED_APPOINTMENTS,
    GET_SUSPECTED_FAKE_APPOINTMENTS,
    GET_PICKUP_NOT_ONLINE_APPOINTMENTS,
    GET_PICKUP_UNSCANNABLE_APPOINTMENTS,
    PickupProblemAppointmentItem,
    PickupProblemAppointmentPage,
} from '../graphql/queries/getPickupFailedAppointments';
import {
    GET_POP_APPOINTMENT_DETAIL,
    GetPopAppointmentDetailResponse,
} from '../graphql/queries/getPopAppointmentDetail';
import TablePaginationBar from '../../../../components/TablePaginationBar';
import SchedulePickupModal from './SchedulePickupModal';
import PopAppointmentDetailPage from '../PopAppointmentDetailPage';
import PickupAppointmentDrawer from '../../appointment/components/PickupAppointmentDrawer';
import { useWarehouseContext } from '../../../../contexts/WarehouseContext';
import { normalizeTimezone } from '../../../../utils/timezoneUtils';
import { useTranslation } from 'react-i18next';

dayjs.extend(utc);
dayjs.extend(timezone);

type DrawerMode = 'failed' | 'fake' | 'notOnline' | 'unscannable';

interface Props {
    visible: boolean;
    onClose: () => void;
    /** 集散中心 Hub ID */
    hubId: number | null;
    /** 揽收日期，默认取进度页当前日期（YYYY-MM-DD） */
    defaultDate: string;
    /**
     * 抽屉模式：
     * failed=揽收失败（无状态/操作列）；fake=虚假上门（含状态/操作列）；
     * notOnline=未上网；unscannable=无法扫描上报。
     * 后两者筛选只有 日期/预约单号/司机/站点 四项（无联系方式），且无操作列。默认 failed
     */
    mode?: DrawerMode;
    /** fake / notOnline 模式下按行司机预筛选（主表格标红列点击带入） */
    initialDriver?: string;
}

/**
 * 预约单状态 → i18n key + 标签色。
 * 配色对齐原型：成功终态绿、失败/取消红、其余进行中橙。
 * i18n 复用既有的 enum.pickupAppointmentStatus.*（预约单列表页同一套），不另起 key。
 */
const APPOINTMENT_STATUS_TAG: Record<string, { i18nKey: string; color: string }> = {
    CREATED: { i18nKey: 'created', color: 'orange' },
    DISPATCHING: { i18nKey: 'dispatching', color: 'orange' },
    DISPATCHED: { i18nKey: 'dispatched', color: 'orange' },
    PICKING_UP: { i18nKey: 'pickingUp', color: 'orange' },
    PICKED_UP: { i18nKey: 'pickedUp', color: 'green' },
    PICKUP_FAILED: { i18nKey: 'pickupFailed', color: 'red' },
    CANCELLED: { i18nKey: 'cancelled', color: 'red' },
};

/** 逗号 / 空格分隔的多值输入拆分为数组 */
const splitMulti = (s: string): string[] =>
    s.split(/[,，\s]+/).map((x) => x.trim()).filter(Boolean);

/** 已提交的筛选快照（翻页复用，避免带上未点「查询」的实时输入） */
interface AppliedFilters {
    date: string;
    orderNos: string;
    driver: string;
    phone: string;
    siteIds: string;
}

/**
 * 揽收进度页「问题预约单」抽屉（揽收失败 / 虚假上门共用）。
 * failed：数据源揽收侧自有 PICKUP_FAILED 单，无状态/操作列。
 * fake：数据源 POP 疑似虚假单，含「状态(待处理/已完结)」+「操作(安排司机上门)」列。
 */
const PickupFailedAppointmentDrawer: React.FC<Props> = ({
    visible, onClose, hubId, defaultDate, mode = 'failed', initialDriver,
}) => {
    const isFake = mode === 'fake';
    const isNotOnline = mode === 'notOnline';
    const isUnscannable = mode === 'unscannable';
    // 「预约单上网情况」两个抽屉：筛选少一项联系方式、无操作列、列组合与失败/虚假不同
    const isOnlineStatusDrawer = isNotOnline || isUnscannable;
    const { t } = useTranslation();
    const tq = (k: string) => t(`pickup.progress.qualityFeedback.${k}`);
    const to = (k: string) => t(`pickup.progress.onlineStatus.${k}`);
    const [date, setDate] = useState<Dayjs | null>(dayjs(defaultDate));
    const [orderNos, setOrderNos] = useState<string>('');
    const [driver, setDriver] = useState<string>('');
    const [phone, setPhone] = useState<string>('');
    const [siteIds, setSiteIds] = useState<string>('');
    const [pageNum, setPageNum] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(20);
    // 已提交的筛选快照：翻页 / 换页大小用它，避免带上"未点查询"的实时输入
    const [applied, setApplied] = useState<AppliedFilters>(
        () => ({ date: defaultDate, orderNos: '', driver: initialDriver || '', phone: '', siteIds: '' }),
    );
    // 复揽「安排司机上门」目标(站点/地址/渠道)。列表行缺 channelId，点按钮时按 appointmentId
    // 拉 getPopAppointmentDetail 补齐后再赋值，保证与详情页「安排司机上门」带入一致。
    const [scheduleTarget, setScheduleTarget] = useState<{ pickupSiteId?: number; address?: string; channelId?: number; appointmentId?: number } | null>(null);
    // 预约单详情:以全屏浮层盖在抽屉之上,返回只关浮层、抽屉与筛选/页码保留
    const [detailApptId, setDetailApptId] = useState<number | undefined>(undefined);
    // 上网情况两个抽屉点预约单号打开的是「预约单详情」抽屉（与预约单列表页同一个），
    // 展示客户/站点/工作时间/运单号/揽收照片/BOL 等完整信息，只读
    const [appointmentDetailId, setAppointmentDetailId] = useState<number | undefined>(undefined);
    // 打开详情那一行的 finished：已完结(=1)说明复揽已执行，详情页不再展示「安排司机上门」。
    // 取列表行的值而非详情接口(详情 DTO 不含 finished)，避免为此再改 pickup-api。
    const [detailFinished, setDetailFinished] = useState<number | undefined>(undefined);

    // 上门时间按 hub 时区展示（arrivedAt 为 UTC 墙钟的 LocalDateTime，与主表格口径一致）
    const { currentWarehouse } = useWarehouseContext();
    const hubTz = normalizeTimezone(currentWarehouse?.timezone || 'UTC-7');
    const formatArrivedAt = (t?: string) => {
        if (!t) return '--';
        const d = dayjs.utc(t).tz(hubTz);
        return d.isValid() ? d.format('YYYY/MM/DD HH:mm') : '--';
    };

    const queryByMode = {
        failed: GET_PICKUP_FAILED_APPOINTMENTS,
        fake: GET_SUSPECTED_FAKE_APPOINTMENTS,
        notOnline: GET_PICKUP_NOT_ONLINE_APPOINTMENTS,
        unscannable: GET_PICKUP_UNSCANNABLE_APPOINTMENTS,
    } as const;
    const [load, { data, loading }] = useLazyQuery<any>(
        queryByMode[mode],
        { fetchPolicy: 'network-only' },
    );

    // 操作列「安排司机上门」：列表行只有 pickupSiteId、无 channelId。点按钮时按 appointmentId 拉详情，
    // 拿到 站点 + 地址 + 渠道 再开弹窗（与详情页「安排司机上门」同一数据源，带入口径一致）。
    const [loadScheduleTarget, { loading: scheduleTargetLoading }] = useLazyQuery<GetPopAppointmentDetailResponse>(
        GET_POP_APPOINTMENT_DETAIL,
        {
            fetchPolicy: 'network-only',
            onCompleted: (d) => {
                const detail = d?.getPopAppointmentDetail;
                if (detail) {
                    setScheduleTarget({
                        pickupSiteId: detail.pickupSiteId,
                        address: detail.siteAddress,
                        channelId: detail.channelId,
                        appointmentId: detail.appointmentId,
                    });
                }
            },
        },
    );

    // 一律基于「已提交快照」发查询，杜绝实时输入串入翻页
    const runQuery = useCallback(
        (page: number, size: number, f: AppliedFilters) => {
            if (!hubId || !f.date) return;
            load({
                variables: {
                    filter: {
                        hubId,
                        pickupDate: f.date,
                        appointmentNumbers: splitMulti(f.orderNos),
                        driverKeyword: f.driver.trim() || null,
                        // 上网情况两个抽屉没有「联系方式」筛选项，恒传 null
                        contactKeyword: isOnlineStatusDrawer ? null : (f.phone.trim() || null),
                        pickupSiteIds: splitMulti(f.siteIds).map(Number).filter((n) => !Number.isNaN(n)),
                        pageNum: page,
                        pageSize: size,
                    },
                },
            });
        },
        [hubId, load],
    );

    // 打开抽屉时重置输入与已提交快照，并按快照首查
    useEffect(() => {
        if (visible) {
            const init: AppliedFilters = { date: defaultDate, orderNos: '', driver: initialDriver || '', phone: '', siteIds: '' };
            setDate(dayjs(defaultDate));
            setOrderNos('');
            setDriver(initialDriver || '');
            setPhone('');
            setSiteIds('');
            setApplied(init);
            setPageNum(1);
            // 清理上次遗留的子层状态，避免重开抽屉时详情/复揽自动弹出
            setScheduleTarget(null);
            setDetailApptId(undefined);
            setDetailFinished(undefined);
            setAppointmentDetailId(undefined);
            runQuery(1, pageSize, init);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, defaultDate, hubId, initialDriver]);

    // 站点ID 仅允许数字（可逗号/空格分隔多个）；有非数字则提示并拦截查询
    const siteIdInvalid = splitMulti(siteIds).some((s) => !/^\d+$/.test(s));

    // 查询：把当前输入固化为已提交快照，回到第 1 页
    const onSearch = () => {
        if (siteIdInvalid) return;
        const snap: AppliedFilters = {
            date: date ? date.format('YYYY-MM-DD') : defaultDate,
            orderNos, driver, phone, siteIds,
        };
        setApplied(snap);
        setPageNum(1);
        runQuery(1, pageSize, snap);
    };

    // 翻页 / 换页大小：一律用已提交快照，不受未提交输入影响
    const onPageChange = (page: number, newSize?: number) => {
        const size = newSize || pageSize;
        setPageNum(page);
        if (newSize && newSize !== pageSize) setPageSize(newSize);
        runQuery(page, size, applied);
    };

    const pageData: PickupProblemAppointmentPage | undefined =
        data?.getSuspectedFakeAppointments
        || data?.getPickupFailedAppointments
        || data?.getPickupNotOnlineAppointments
        || data?.getPickupUnscannableAppointments;
    const rows = pageData?.content || [];
    const total = pageData?.totalElements || 0;

    const baseColumns = [
        {
            title: tq('colAppointment'), dataIndex: 'appointmentNumber', width: 170,
            render: (v: string, row: PickupProblemAppointmentItem) => (
                v
                    ? <Button type="link" style={{ paddingLeft: 0 }} onClick={() => { setDetailApptId(row.appointmentId); setDetailFinished(row.finished); }}>
                        <span style={{ whiteSpace: 'nowrap' }}>{v}</span>
                      </Button>
                    : <span style={{ whiteSpace: 'nowrap' }}>--</span>
            ),
        },
        { title: tq('colTaskId'), dataIndex: 'taskNumber', width: 180, render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{v || '--'}</span> },
        { title: tq('colSite'), dataIndex: 'pickupSiteName', width: 130, render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{v || '--'}</span> },
        { title: tq('colAddress'), dataIndex: 'address', width: 200, render: (v: string) => v || '--' },
        { title: tq('colDriver'), dataIndex: 'driverName', width: 90, render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{v || '--'}</span> },
        { title: tq('colContact'), dataIndex: 'driverPhone', width: 140, render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{v || '--'}</span> },
    ];

    // 上门时间取「实际揽收成功时间」，仅虚假上门有值；揽收失败单无成功时间，故失败抽屉不展示该列
    const arrivedAtColumn = {
        title: tq('colArrivedAt'), dataIndex: 'arrivedAt', width: 160,
        render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{formatArrivedAt(v)}</span>,
    };

    const fakeColumns = [
        {
            title: tq('colStatus'), dataIndex: 'finished', width: 90,
            render: (v: number) =>
                v === 1
                    ? <Tag color="green">{tq('finished')}</Tag>
                    : <Tag color="orange">{tq('pending')}</Tag>,
        },
        {
            // 操作列右侧锁定：抽屉一打开即可见，其余列随横向滚动
            title: tq('colAction'), width: 130, fixed: 'right' as const,
            // PRD 4.3：状态为已完结(finished=1)时不展示「安排司机上门」
            render: (_: any, row: PickupProblemAppointmentItem) => (
                row.finished === 1 ? (
                    <span style={{ whiteSpace: 'nowrap' }}>--</span>
                ) : (
                    <Button
                        type="link"
                        style={{ paddingLeft: 0 }}
                        loading={scheduleTargetLoading}
                        onClick={() => loadScheduleTarget({ variables: { appointmentId: row.appointmentId } })}
                    >
                        {tq('schedule')}
                    </Button>
                )
            ),
        },
    ];

    // 预约单号列：上网情况两个抽屉列名是「预约单号」，且点开的是预约单详情抽屉
    // （失败/虚假点开的是 POP 问题单详情页，那套只有审核信息与异常照片，对这两个场景没意义）
    const appointmentNumberColumn = {
        title: to('colAppointmentNumber'), dataIndex: 'appointmentNumber', width: 170,
        render: (value: string, row: PickupProblemAppointmentItem) => (
            value
                ? <Button type="link" style={{ paddingLeft: 0 }} onClick={() => setAppointmentDetailId(row.appointmentId)}>
                    <span style={{ whiteSpace: 'nowrap' }}>{value}</span>
                  </Button>
                : <span style={{ whiteSpace: 'nowrap' }}>--</span>
        ),
    };

    /** 站点 ID / 站点名称：上网情况抽屉把站点拆成两列（失败/虚假只有一列「揽收站点」） */
    const siteColumns = [
        {
            title: to('colSiteId'), dataIndex: 'pickupSiteId', width: 110,
            render: (value: number) => <span style={{ whiteSpace: 'nowrap' }}>{value ?? '--'}</span>,
        },
        {
            title: to('colSiteName'), dataIndex: 'pickupSiteName', width: 140,
            render: (value: string) => <span style={{ whiteSpace: 'nowrap' }}>{value || '--'}</span>,
        },
    ];

    const notOnlineTailColumns = [
        { title: to('colAddress'), dataIndex: 'address', width: 200, render: (value: string) => value || '--' },
        { title: to('colTask'), dataIndex: 'taskNumber', width: 180, render: (value: string) => <span style={{ whiteSpace: 'nowrap' }}>{value || '--'}</span> },
        { title: to('colDriver'), dataIndex: 'driverName', width: 90, render: (value: string) => <span style={{ whiteSpace: 'nowrap' }}>{value || '--'}</span> },
        { title: to('colContact'), dataIndex: 'driverPhone', width: 140, render: (value: string) => <span style={{ whiteSpace: 'nowrap' }}>{value || '--'}</span> },
    ];

    /**
     * 按 mode 组装列，避免多层三元嵌套：
     * - failed：预约单 / 揽收任务ID / 揽收站点 / 地址 / 司机 / 联系方式
     * - fake：failed 各列 + 上门时间 + 状态 + 操作(安排司机上门)
     * - notOnline：预约单号 / 状态 / 站点ID / 站点名称 / 地址 / 揽收任务 / 司机 / 联系方式
     * - unscannable：notOnline 的列，但把状态列换成「审核结果」(隐藏占位) + 「无法扫描原因」
     */
    const buildColumns = (m: DrawerMode) => {
        if (m === 'notOnline') {
            return [
                appointmentNumberColumn,
                {
                    title: to('colStatus'), dataIndex: 'appointmentStatus', width: 110,
                    render: (value: string) => {
                        const tag = APPOINTMENT_STATUS_TAG[value];
                        if (!tag) {
                            return <span style={{ whiteSpace: 'nowrap' }}>{value || '--'}</span>;
                        }
                        return (
                            <Tag color={tag.color}>
                                <span style={{ whiteSpace: 'nowrap' }}>{t(`enum.pickupAppointmentStatus.${tag.i18nKey}`)}</span>
                            </Tag>
                        );
                    },
                },
                ...siteColumns,
                ...notOnlineTailColumns,
            ];
        }
        if (m === 'unscannable') {
            return [
                appointmentNumberColumn,
                {
                    // 审核结果由审核团队(quality)给出，本期未介入 → 隐藏占位。
                    // 字段名与 i18n key 已按最终形态锁定，接审核时去掉 hidden 即可，无需改名。
                    title: to('colReviewStatus'), dataIndex: 'unscannableReviewStatus', width: 120,
                    hidden: true,
                    render: (value: string) => <span style={{ whiteSpace: 'nowrap' }}>{value || '--'}</span>,
                },
                {
                    title: to('colUnscannableReason'), dataIndex: 'unscannableReason', width: 200,
                    render: (value: string) => value || '--',
                },
                ...siteColumns,
                ...notOnlineTailColumns,
            ];
        }
        // 虚假上门：...基础列 + 上门时间 + 状态/操作；揽收失败：基础列(不含上门时间)
        return m === 'fake' ? [...baseColumns, arrivedAtColumn, ...fakeColumns] : baseColumns;
    };

    const columns = buildColumns(mode);

    const titleByMode: Record<DrawerMode, string> = {
        failed: tq('failedTitle'),
        fake: tq('fakeTitle'),
        notOnline: to('notOnlineTitle'),
        unscannable: to('unscannableTitle'),
    };
    const drawerTitle = titleByMode[mode];
    // 上网情况抽屉列更多（8~9 列），与原型一致给 980
    const drawerWidth = isOnlineStatusDrawer ? 980 : (isFake ? 1040 : 900);

    return (
        <Drawer title={drawerTitle} open={visible} onClose={onClose} width={drawerWidth} destroyOnClose>
            <div className={`grid ${isOnlineStatusDrawer ? 'grid-cols-4' : 'grid-cols-5'} gap-3 mb-4 items-end`}>
                <div>
                    <div className="text-xs text-gray-500 mb-1">{tq('filterDate')}</div>
                    <DatePicker value={date} onChange={setDate} format="YYYY/MM/DD" allowClear={false} className="w-full" />
                </div>
                <div>
                    <div className="text-xs text-gray-500 mb-1">{tq('filterOrderNos')}</div>
                    <Input placeholder={tq('multiHint')} value={orderNos} onChange={(e) => setOrderNos(e.target.value)} />
                </div>
                <div>
                    <div className="text-xs text-gray-500 mb-1">{tq('filterDriver')}</div>
                    <Input placeholder={tq('fuzzyHint')} value={driver} onChange={(e) => setDriver(e.target.value)} />
                </div>
                {/* 上网情况两个抽屉的筛选只有 日期/预约单号/司机/站点 四项，不含联系方式 */}
                {!isOnlineStatusDrawer && (
                    <div>
                        <div className="text-xs text-gray-500 mb-1">{tq('filterContact')}</div>
                        <Input placeholder={tq('fuzzyHint')} value={phone} onChange={(e) => setPhone(e.target.value)} />
                    </div>
                )}
                <div className="flex items-end gap-2">
                    <div className="flex-1">
                        <div className="text-xs text-gray-500 mb-1">{tq('filterSiteId')}</div>
                        <Input
                            placeholder={tq('multiHint')}
                            value={siteIds}
                            status={siteIdInvalid ? 'error' : undefined}
                            onChange={(e) => setSiteIds(e.target.value)}
                            onPressEnter={onSearch}
                        />
                        {siteIdInvalid && (
                            <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>{tq('siteIdNumericHint')}</div>
                        )}
                    </div>
                    <Button type="primary" onClick={onSearch} disabled={siteIdInvalid}>{tq('search')}</Button>
                </div>
            </div>

            <Table<PickupProblemAppointmentItem>
                loading={loading}
                rowKey="appointmentId"
                size="middle"
                scroll={{ x: 'max-content' }}
                dataSource={rows}
                pagination={false}
                columns={columns as any}
            />
            <TablePaginationBar
                total={total}
                current={pageNum}
                pageSize={pageSize}
                onChange={onPageChange}
                showQuickJumper
                pageSizeOptions={['20', '50', '100']}
            />

            {/* 复揽只在虚假上门抽屉有入口；上网情况两个抽屉无操作列，不渲染 */}
            {!isOnlineStatusDrawer && (
                <SchedulePickupModal
                    visible={!!scheduleTarget}
                    onClose={() => setScheduleTarget(null)}
                    target={scheduleTarget}
                    baseDate={date ? date.format('YYYY-MM-DD') : defaultDate}
                />
            )}

            {/* 上网情况两个抽屉：点预约单号打开预约单详情（与预约单列表页同一个抽屉，只读） */}
            {isOnlineStatusDrawer && (
                <PickupAppointmentDrawer
                    visible={appointmentDetailId != null}
                    onClose={() => setAppointmentDetailId(undefined)}
                    appointmentId={appointmentDetailId}
                    mode="detail"
                />
            )}

            {/* 失败/虚假抽屉的 POP 问题单详情：全屏浮层盖在抽屉之上；返回箭头只关浮层，抽屉与筛选/页码保留 */}
            <Drawer
                open={detailApptId != null}
                onClose={() => setDetailApptId(undefined)}
                width="100%"
                closable={false}
                destroyOnClose
                styles={{ body: { padding: 0, background: '#f5f5f5' } }}
            >
                {detailApptId != null && (
                    <PopAppointmentDetailPage
                        appointmentId={detailApptId}
                        onBack={() => setDetailApptId(undefined)}
                        allowSchedule={isFake && detailFinished !== 1}
                    />
                )}
            </Drawer>
        </Drawer>
    );
};

export default PickupFailedAppointmentDrawer;
